import React from 'react';
import { CloseIcon } from '../common/CloseIcon';
import { Trans, useTranslation } from 'react-i18next';
import { ImageData } from '../common/PropertyImageUpload';

import '../MessagesModal/MessagesModal.scss';
import './BuyingTemplateIndicator.scss';
import './OffMarketBuyAndSellDetailsModal.scss';

export interface PropertyDetails {
  description?: string;
  size?: number;
  rooms?: string;
  floor?: number;
  hasElevator?: boolean;
  hasBalcony?: boolean;
  renovationStatus?: string;
  buildingType?: string;
  images?: ImageData[];
  askingPrice?: number;
  priceEstimationId?: string;
  priceEstimationStatus?: 'idle' | 'queued' | 'in_progress' | 'completed' | 'failed' | 'error';
}

type Props = {
  updateFormField: (field: "description" | "size" | "rooms" | "floor" | "hasElevator" | "hasBalcony" | "renovationStatus" | "buildingType" | "images" | "timestamp", value: any) => void;
  setIsPreEstimationModalOpen: (isOpen: boolean) => void;
  startEstimation: () => void;
  setForceShowPreFields: (forceShowPreFields: boolean) => void;
  formData: {
    description: string;
    size: string;
    rooms: string;
    floor: string;
    hasElevator: boolean;
    hasBalcony: boolean;
    renovationStatus: string;
    buildingType: string;
  };
  setSizeError: (sizeError: string) => void;
  sizeError: string;
  isLoggedIn: boolean;
  setLoginModalVisible: (isVisible: boolean) => void;
};

const ROOM_OPTIONS = ['1', '2', '3', '4', '5+'];

const BUILDING_TYPE_OPTIONS = [
  { value: 'apartment_building', label: 'building.offMarket.modal.apartmentBuilding' },
  { value: 'terraced_house', label: 'building.offMarket.modal.terracedHouse' },
  { value: 'duplex', label: 'building.offMarket.modal.duplex' },
  { value: 'detached_house', label: 'building.offMarket.modal.detachedHouse' },
  { value: 'other', label: 'building.offMarket.modal.other' },
];

const RENOVATION_STATUS_OPTIONS = [
  { value: 'recently_renovated', label: 'building.offMarket.modal.recentlyRenovated' },
  { value: 'good_condition', label: 'building.offMarket.modal.goodCondition' },
  { value: 'needs_renovation', label: 'building.offMarket.modal.needsRenovation' },
];

const PreEstimationModal: React.FC<Props> = ({
  setIsPreEstimationModalOpen,
  updateFormField,
  startEstimation,
  setForceShowPreFields,
  formData,
  setSizeError,
  sizeError,
  isLoggedIn,
  setLoginModalVisible,
}) => {
  const { t } = useTranslation();

  return (
    <div className="pre-estimation-modal-overlay" onClick={() => { setIsPreEstimationModalOpen(false); setForceShowPreFields(false); }}>
      <div className="pre-estimation-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="pre-estimation-modal__close"
          onClick={() => { setIsPreEstimationModalOpen(false); setForceShowPreFields(false); }}
        >
          <CloseIcon color="#202125" size={20} />
        </button>

        <div className="pre-estimation-modal__gradient">
          <div className="pre-estimation-modal__content">
            <div className="pre-estimation-modal__icon">
              <img
                className="pre-estimation-modal__ai-icon"
                src="/assets/jaakko_head.png"
                height="48px"
                alt="Jaakko AI"
              />
              <svg className="pre-estimation-modal__sparkle" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2l2.09 6.26L20 10l-6.26 2.09L12 20l-2.09-6.26L4 12l6.26-2.09L12 2z" fill="#F0CD00" />
              </svg>
            </div>

            <h3 className="pre-estimation-modal__title">
              {t('building.offMarket.modal.preEstimationTitle')}
            </h3>

            <p className="pre-estimation-modal__subtitle">
              {t('building.offMarket.modal.preEstimationSubtitle')}
            </p>
            <div className="pre-estimation-modal__form">
              <div className="pre-estimation-modal__field">
                <label className="pre-estimation-modal__label">
                  {t('building.offMarket.modal.size')}
                  <span className="pre-estimation-modal__required">*</span>
                </label>
                <input
                  type="number"
                  className="pre-estimation-modal__input"
                  value={formData.size}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormField('size', value);
                    const isInvalid = value.trim() !== '' && !(Number(value) > 0);
                    setSizeError(isInvalid ? t('building.offMarket.modal.sizeMustBeGreaterThanZero') : '');
                  }}
                  placeholder={t('building.offMarket.modal.sizePlaceholder')}
                  min="1"
                />
                {sizeError && (
                  <div className="pre-estimation-modal__error" role="alert">
                    {sizeError}
                  </div>
                )}
              </div>

              <div className="pre-estimation-modal__field">
                <label className="pre-estimation-modal__label">
                  {t('building.offMarket.modal.roomsLabel')}
                  <span className="pre-estimation-modal__required">*</span>
                </label>
                <select
                  className="pre-estimation-modal__select"
                  value={formData.rooms}
                  onChange={(e) => updateFormField('rooms', e.target.value)}
                >
                  <option value="">{t('building.offMarket.modal.selectRooms')}</option>
                  {ROOM_OPTIONS.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>

              <div className="pre-estimation-modal__field">
                <label className="pre-estimation-modal__label">
                  {t('building.offMarket.modal.buildingType')}
                  <span className="pre-estimation-modal__required">*</span>
                </label>
                <select
                  className="pre-estimation-modal__select"
                  value={formData.buildingType}
                  onChange={(e) => updateFormField('buildingType', e.target.value)}
                >
                  <option value="">{t('building.offMarket.modal.selectBuildingType')}</option>
                  {BUILDING_TYPE_OPTIONS.map((type) => (
                    <option key={type.value} value={type.value}>
                      {t(type.label)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pre-estimation-modal__tip">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="#667eea" />
                </svg>
                <span>{t('building.offMarket.modal.preEstimationTip')}</span>
              </div>

              <div className="off-market-modal__form-field" style={{ marginTop: 16 }}>
                <label htmlFor="pre-estimation-description">
                  <Trans i18nKey="building.offMarket.modal.description" />
                </label>
                <textarea
                  id="pre-estimation-description"
                  className="off-market-modal__textarea"
                  value={formData.description}
                  onChange={(e) => updateFormField('description', e.target.value)}
                  placeholder={t('building.offMarket.modal.sellingInterestDescriptionPlaceholder')}
                  rows={3}
                  maxLength={1100}
                />
                <span className="off-market-modal__textarea-counter">{formData.description.length} / 1100</span>
              </div>

              <div className="off-market-modal__form-row">
                <div className="off-market-modal__form-field off-market-modal__form-field--half">
                  <label htmlFor="pre-estimation-floor">
                    <Trans i18nKey="building.offMarket.modal.floor" />
                  </label>
                  <input
                    id="pre-estimation-floor"
                    type="number"
                    className="off-market-modal__input"
                    value={formData.floor}
                    onChange={(e) => updateFormField('floor', e.target.value)}
                    placeholder={t('building.offMarket.modal.floorPlaceholder')}
                    min="0"
                  />
                </div>

                <div className="off-market-modal__form-field off-market-modal__form-field--half">
                  <label htmlFor="pre-estimation-renovation">
                    <Trans i18nKey="building.offMarket.modal.renovation" />
                  </label>
                  <select
                    id="pre-estimation-renovation"
                    className="off-market-modal__select"
                    value={formData.renovationStatus}
                    onChange={(e) => updateFormField('renovationStatus', e.target.value)}
                  >
                    <option value="">
                      {t('building.offMarket.modal.selectRenovation')}
                    </option>
                    {RENOVATION_STATUS_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>
                        {t(status.label)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="off-market-modal__form-row off-market-modal__form-row--checkboxes">
                <div className="off-market-modal__form-field off-market-modal__form-field--checkbox">
                  <input
                    id="pre-estimation-elevator"
                    type="checkbox"
                    checked={formData.hasElevator}
                    onChange={(e) => updateFormField('hasElevator', e.target.checked)}
                  />
                  <label htmlFor="pre-estimation-elevator">
                    <Trans i18nKey="building.offMarket.modal.hasElevator" />
                  </label>
                </div>

                <div className="off-market-modal__form-field off-market-modal__form-field--checkbox">
                  <input
                    id="pre-estimation-balcony"
                    type="checkbox"
                    checked={formData.hasBalcony}
                    onChange={(e) => updateFormField('hasBalcony', e.target.checked)}
                  />
                  <label htmlFor="pre-estimation-balcony">
                    <Trans i18nKey="building.offMarket.modal.hasBalcony" />
                  </label>
                </div>
              </div>
            </div>

            <div className="pre-estimation-modal__actions">
              <button
                className="pre-estimation-modal__button pre-estimation-modal__button--secondary"
                onClick={() => { setIsPreEstimationModalOpen(false); setForceShowPreFields(false); }}
              >
                <Trans i18nKey="building.offMarket.modal.preEstimationBack" />
              </button>
              <button
                className="pre-estimation-modal__button pre-estimation-modal__button--primary"
                onClick={() => {
                  if (!isLoggedIn) {
                    // Defer marketing consent for this browser session so
                    // it doesn't interrupt the estimation flow after auth.
                    // The user still explicitly starts the estimate after
                    // logging in.
                    sessionStorage.setItem('marketing_consent_deferred', '1');
                    if (setLoginModalVisible) {
                      setLoginModalVisible(true);
                    }
                    return;
                  }
                  setIsPreEstimationModalOpen(false);
                  setForceShowPreFields(false);
                  startEstimation();
                }}
                disabled={
                  !(Number(formData.size) > 0) ||
                  !formData.rooms.trim() ||
                  !formData.buildingType.trim()
                }
              >
                <span>{t('building.offMarket.modal.startEstimation')}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <p className="pre-estimation-modal__footer-text">
              {t('building.offMarket.modal.estimationTime')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreEstimationModal;